
(function () {
  'use strict';
 
  var complementaryCache = {};
  var complementaryCacheKeys = [];
  var CACHE_LIMIT = 12;

  function cacheRead(key) {
    return Object.prototype.hasOwnProperty.call(complementaryCache, key)
      ? complementaryCache[key]
      : null;
  }

  function cacheWrite(key, value) {
    if (!Object.prototype.hasOwnProperty.call(complementaryCache, key)) {
      complementaryCacheKeys.push(key);
      if (complementaryCacheKeys.length > CACHE_LIMIT) {
        delete complementaryCache[complementaryCacheKeys.shift()];
      }
    }
    complementaryCache[key] = value;
  }

  class ProductComplementary extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.dataset.state === 'loaded') return;

      /* Server-rendered content (the recommendations endpoint itself). */
      if (this.querySelector('[data-complementary-content]')) {
        this.dataset.state = 'loaded';
        this.initCarousel();
        return;
      }
 
      var cached = cacheRead(this.cacheKey());
      if (cached) {
        this.dataset.state = 'loaded';
        if (cached === 'empty') {
          this.applyEmpty();
        } else {
          this.applyContent(cached);
        }
        return;
      }
 
      if (window.Shopify && window.Shopify.designMode) {
        this.loadRecommendations();
        return;
      }

      var self = this;
      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(
          function (entries) {
            if (!entries[0].isIntersecting) return;
            self._observer.disconnect();
            self._observer = null;
            self.loadRecommendations();
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        this._observer.observe(this);
      } else {
        this.loadRecommendations();
      }
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      if (this._controller) this._controller.abort();
      this.destroyCarousel();
    }

    cacheKey() {
      return this.dataset.url + '|' + (this.dataset.cacheKey || '');
    }

    loadRecommendations() {
      if (this.dataset.state === 'loading' || this.dataset.state === 'loaded') return;

      var key = this.cacheKey();
      var cached = cacheRead(key);
      if (cached) {
        this.dataset.state = 'loaded';
        if (cached === 'empty') this.applyEmpty();
        else this.applyContent(cached);
        return;
      }

      this.dataset.state = 'loading';

      if (this._controller) this._controller.abort();
      this._controller = new AbortController();

      var self = this;
      fetch(this.dataset.url, { signal: this._controller.signal })
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector(
            'product-complementary[data-block-id="' + self.dataset.blockId + '"]'
          );
          var content = fresh && fresh.querySelector('[data-complementary-content]');
          self.dataset.state = 'loaded';
          if (content) {
            cacheWrite(key, content.outerHTML);
            self.applyContent(content.outerHTML);
          } else {
            cacheWrite(key, 'empty');
            self.applyEmpty();
          }
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          self.dataset.state = 'loaded';
          self.applyEmpty();
        });
    }

    applyContent(html) {
      this.innerHTML = html;
      this.hidden = false;
      this.initCarousel();
    }

    initCarousel(attempts) {
      var el = this.querySelector('[data-product-complementary-swiper]');
      if (!el || el.swiperInstance) return;
 
      if (!window.Swiper) {
        attempts = attempts || 0;
        if (attempts >= 40 || !this.isConnected) return;
        var self = this;
        setTimeout(function () {
          self.initCarousel(attempts + 1);
        }, 100);
        return;
      }

      var colsDesktop = parseInt(el.dataset.colsDesktop, 10) || 2;
      var colsTablet = parseInt(el.dataset.colsTablet, 10) || 2;
      var colsMobile = parseInt(el.dataset.colsMobile, 10) || 2;
      var gap = parseInt(el.dataset.gap, 10) || 12;
      var navStyle = el.dataset.nav || 'navigation';

      var opts = {
        slidesPerView: colsMobile,
        spaceBetween: gap,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        a11y: { slideRole: 'listitem' },
        breakpoints: {}
      };
      opts.breakpoints[0] = { slidesPerView: colsMobile, spaceBetween: gap };
      opts.breakpoints[576] = { slidesPerView: colsTablet, spaceBetween: gap };
      opts.breakpoints[992] = { slidesPerView: colsDesktop, spaceBetween: gap };

      if (navStyle === 'navigation' || navStyle === 'both') {
        opts.navigation = {
          nextEl: el.querySelector('.swiper-button-next'),
          prevEl: el.querySelector('.swiper-button-prev')
        };
      }
      if (navStyle === 'pagination' || navStyle === 'both') {
        var pagEl = el.querySelector('.swiper-pagination');
        opts.pagination = { el: pagEl, clickable: true };
 
        if (pagEl && pagEl.dataset.paginationStyle === 'bold') {
          opts.pagination.renderBullet = function (index, className) {
            return (
              '<span class="' + className + ' custom-bullet">' +
              '<svg class="progress-ring" viewBox="0 0 34 34" aria-hidden="true">' +
              '<circle class="progress-ring-bg" cx="17" cy="17" r="16"></circle>' +
              '<circle class="progress-ring-fill" cx="17" cy="17" r="16"></circle>' +
              '</svg>' +
              '<span class="bullet-dot"></span>' +
              '</span>'
            );
          };
        }
      }

      el.swiperInstance = new window.Swiper(el, opts);
    }

    destroyCarousel() {
      var el = this.querySelector('[data-product-complementary-swiper]');
      if (el && el.swiperInstance) {
        el.swiperInstance.destroy(true, true);
        el.swiperInstance = null;
      }
    }

    /* No complementary products: keep the hint in the editor, hide on storefront. */
    applyEmpty() {
      var skeleton = this.querySelector('[data-complementary-skeleton]');
      if (skeleton) skeleton.remove();

      if (window.Shopify && window.Shopify.designMode) {
        var hint = this.querySelector('[data-complementary-hint]');
        if (hint) hint.hidden = false;
        return;
      }
      this.hidden = true;
    }
  }

  if (!customElements.get('product-complementary')) {
    customElements.define('product-complementary', ProductComplementary);
  }
 
  document.addEventListener('shopify:block:select', function (event) {
    var target = event.target;
    if (!target || !target.matches) return;
 
    var el = target.matches('product-complementary')
      ? target
      : target.querySelector('product-complementary');

    if (el && typeof el.loadRecommendations === 'function') el.loadRecommendations();
  });
})();
